import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi, UpdateCompanyData, CreateSeriesData, CreateBankAccountData } from '../services/settings-api';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { toast } from 'sonner';

// Company (formerly Tenant — terminologie consistentă cu UI „Companie"/„Firmă")
export function useCompany() {
  return useQuery({
    queryKey: ['company'],
    queryFn: () => settingsApi.getCompany(),
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCompanyData) => settingsApi.updateCompany(data),
    onSuccess: (updatedCompany) => {
      // Setăm cache-ul React Query CU valoarea returnată de backend (nu doar
      // invalidate). Asta previne ca alte pagini să vadă valoarea veche din
      // cache pentru o fracțiune de secundă la navigare rapidă post-save
      // (ex: /setari → /facturi/nou afișează încă numele vechi al firmei).
      qc.setQueryData(['company'], updatedCompany);
      qc.invalidateQueries({ queryKey: ['company'] });
      // Sincronizăm authStore (Zustand) — conține tenant.name folosit în
      // sidebar, header, etc. fără refresh, ar rămâne valoarea veche până
      // la următorul login.
      useAuthStore.getState().refreshProfile().catch(() => {});
      toast.success('Setarile au fost salvate');
    },
    // Afișăm motivul real din backend (ex. „Forma legală are o valoare invalidă…”), nu un
    // mesaj generic — altfel userul nu are cum să afle CE anume blochează salvarea.
    onError: (err: any) => toast.error(err?.message || 'Eroare la salvarea setarilor'),
  });
}

// ── Backward-compat aliases (deprecated — vor fi șterse după Etapa 3) ──
/** @deprecated Use useCompany() */
export const useTenant = useCompany;
/** @deprecated Use useUpdateCompany() */
export const useUpdateTenant = useUpdateCompany;

// Invoice Series
export function useInvoiceSeries() {
  return useQuery({
    queryKey: ['invoice-series'],
    queryFn: () => settingsApi.getSeries(),
  });
}

export function useCreateSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSeriesData) => settingsApi.createSeries(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice-series'] });
      toast.success('Serie creată');
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la crearea seriei'),
  });
}

export function useUpdateSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSeriesData & { isActive?: boolean }> }) =>
      settingsApi.updateSeries(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice-series'] });
      toast.success('Serie actualizată');
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la actualizarea seriei'),
  });
}

export function useDeleteSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteSeries(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice-series'] });
      toast.success('Serie ștearsă');
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la ștergerea seriei'),
  });
}

// Bank Accounts
export function useBankAccounts() {
  return useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => settingsApi.getBankAccounts(),
  });
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBankAccountData) => settingsApi.createBankAccount(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      toast.success('Cont bancar adăugat');
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la adaugarea contului'),
  });
}

export function useUpdateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateBankAccountData & { isActive?: boolean }> }) =>
      settingsApi.updateBankAccount(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      toast.success('Cont bancar actualizat');
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la actualizarea contului'),
  });
}

export function useDeleteBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteBankAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      toast.success('Cont bancar sters');
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la ștergerea contului'),
  });
}

export function useSetDefaultBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => settingsApi.setDefaultBankAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      toast.success('Cont implicit setat');
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la setarea contului implicit'),
  });
}
